import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';

import { ClassNames } from '../shared/icon-font/icon-font-classes.utility';
import { IconFontType } from '../shared/icon-font/icon-font-type.enum';
import { KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { EventMetric, UserProfileService } from '../shared/instrumentation/user-profile.service';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { DateService } from '../shared/services/date.service';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { AppEvent, EventService } from '../shared/services/event.service';
import { WebPwaService } from '../shared/services/web-pwa.service';
import { WindowService } from '../shared/services/window.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { LocService } from '../shared/shared-context/loc.service';
import { UserContextService } from '../shared/shared-context/user-context.service';
import { AnnouncementType } from './announcement-type.enum';
import { AnnouncementModel } from './announcement.model';
import { BannerConfigService } from './banner-config.service';

@Injectable({
    providedIn: 'root'
})
export class AnnouncementService {

    private readonly announcements: Array<AnnouncementModel> = [];
    constructor(
        private readonly bannerConfigService: BannerConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly deviceDetector: DeviceDetector,
        private readonly userContextService: UserContextService,
        private readonly locService: LocService,
        private readonly dateService: DateService,
        private readonly userProfileService: UserProfileService,
        private readonly webPwaService: WebPwaService,
        private readonly windowService: WindowService,
        private readonly eventService: EventService
    ) {
    }

    public registerKnownAnnouncements(announcementType: AnnouncementType): void {
        if (!this.bannerConfigService.config) {
            return;
        }

        const announcements: Array<AnnouncementModel> = [];
        const resources = this.bannerConfigService.config.resources;
        const iconClass = ClassNames.FluentIconCssClass;
        const trialUserAnnouncementSubTitle = this.locService.format(resources.TrialUserAnnouncementSubTitle, `<b>${resources.TrialAnnouncementBuyButtonText}</b>`);
        switch (announcementType) {
            case AnnouncementType.TrialSubscription: {
                const announcement = new AnnouncementModel(announcementType, resources.TrialUserAnnouncementTitle, trialUserAnnouncementSubTitle, `${iconClass} ${ClassNames.getCssClass(IconFontType.Alert)}`, 'trialBannerCookie');
                announcement.primaryButton = { id: 'trial-announcement-buy-office', text: resources.TrialAnnouncementBuyButtonText, ariaLabel: resources.TrialAnnouncementBuyButtonAriaLabel, url: 'https://portal.office.com/Commerce/Catalog.aspx?source=home' };
                announcement.secondaryButton = { id: 'trial-announcement-remind-later', text: resources.TrialAnnouncementDelayButtonText, ariaLabel: resources.DismissAnnouncementAriaLabel };
                announcement.secondaryButton.dismissOnClick = true;
                announcements.push(announcement);
                break;
            }
            case AnnouncementType.MissingPuid: {
                const announcement = new AnnouncementModel(announcementType, resources.MissingPuidAnnouncementTitle, resources.MissingPuidAnnouncementSubTitle, `${iconClass} ${ClassNames.getCssClass(IconFontType.Alert)}`);
                announcements.push(announcement);
                break;
            }
            case AnnouncementType.NoLicense: {
                if (this.userContextService.user.isAdmin) {
                    const announcement = new AnnouncementModel(AnnouncementType.NoLicenseAdmin1, resources.NoLicenseAdminViewTitle, resources.NoLicenseAdminViewSubTitle, `${iconClass} ${ClassNames.getCssClass(IconFontType.Alert)}`);
                    const announcement2 = new AnnouncementModel(AnnouncementType.NoLicenseAdmin2, resources.NoLicenseAdminView2Title, resources.NoLicenseAdminView2SubTitle, `${iconClass} ${ClassNames.getCssClass(IconFontType.Alert)}`);
                    announcement.primaryButton = { id: 'no-license-admin-assign-product', text: resources.AssignProductsButtonText, ariaLabel: resources.AssignProductsButtonAriaLabel, url: `${this.bannerConfigService.config.resources.AssignProductsButtonUrl}` };
                    announcements.push(announcement);
                    announcements.push(announcement2);
                } else {
                    const announcement = new AnnouncementModel(announcementType, resources.NoLicenseUserViewTitle, resources.NoLicenseUserViewSubTitle, `${iconClass} ${ClassNames.getCssClass(IconFontType.Alert)}`);
                    announcements.push(announcement);
                }
                break;
            }
            case AnnouncementType.IE11EndOfSupport: {
                const fontCssClass = 'announcement-section__label--IE11EOS announcement-close__button--IE11EOS';
                const backgroundCssClass = 'announcement-background-color-wrapper--IE11EOS';
                const announcement = new AnnouncementModel(
                    AnnouncementType.IE11EndOfSupport,
                    resources.IE11EOSAnnouncementTitle,
                    resources.IE11EOSAnnouncementSubTitle,
                    `${iconClass} ${ClassNames.getCssClass(IconFontType.Alert)} announcement-section__icon--IE11EOS`,
                    '',
                    false,
                    backgroundCssClass,
                    fontCssClass,
                    null,
                    true,
                    null,
                    null,
                    true);
                announcement.borderlessButton = {
                    id: 'trial-announcement-buy-office',
                    text: resources.IE11EOSButtonText,
                    ariaLabel: resources.IE11EOSButtonAriaLabel,
                    url: resources.IE11EndOfSupportRedirectUrl
                };
                announcements.push(announcement);
                break;
            }
            default:
                break;
        }
        this.registerAnnouncements(announcements);
    }

    public registerAnnouncements(announcementModels: Array<AnnouncementModel>): void {
        this.announcements.push(...announcementModels);
    }

    public setUpAddToHomeScreenPrompt(viewMetric: EventMetric): Promise<boolean> {
        if (this.capabilitiesService.capabilities.showA2HSPrompt) {
            return Promise.resolve(true);
        }
        return this.capabilitiesService.capabilities.appManifestEnabled ? Promise.all([this.userProfileService.get(), this.webPwaService.isStandalone.pipe(take(1)).toPromise()]).then(([profile, isStandalone]) => {
            if (isStandalone) {
                return false;
            }

            const isMobile = this.deviceDetector.isMobileDevice();
            // Show the prompt once a week if they have been to the site at least once on mobile within the last two weeks
            const metrics = profile.metrics;
            const appAddedMetric = metrics[isMobile ? KnownInstrumentationEvents.AppAddedToHomeScreenMobile : KnownInstrumentationEvents.AppAddedToHomeScreenDesktop];
            const addPromptMetric = metrics[isMobile ? KnownInstrumentationEvents.AddToHomeScreenPromptImpressionMobile : KnownInstrumentationEvents.AddToHomeScreenPromptImpressionDesktop];
            // Respect old app install metric so we don't spam users who have already installed
            const oldAddedMetric = metrics[KnownInstrumentationEvents.AppAddedToHomeScreen];

            if ((appAddedMetric && appAddedMetric.count > 0) || oldAddedMetric && oldAddedMetric.count > 0) {
                return false;
            }

            if (isMobile) {
                const oneWeekAgo = this.dateService.addDays(this.dateService.getCurrentDate(), -7).getTime();
                const twoWeeksAgo = this.dateService.addDays(this.dateService.getCurrentDate(), -14).getTime();
                const oneWeekSinceSeenLast = !addPromptMetric || addPromptMetric.lastOccurrence <= oneWeekAgo;
                const visitedRecently = viewMetric && viewMetric.lastOccurrence >= twoWeeksAgo;
                return oneWeekSinceSeenLast && visitedRecently;
            }

            return !(addPromptMetric && addPromptMetric.count > 0);
        }) : Promise.resolve(false);
    }

    public setUpSessionExpiredAnnouncement() {
        this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter((event: AppEvent) => event.name === AngularEvents.sessionExpired)
        ).subscribe(() => {
            const { resources } = this.bannerConfigService.config;

            const announcement = new AnnouncementModel(
                AnnouncementType.SessionExpired,
                resources.SessionExpiredAnnouncementTitle,
                undefined,
                `${ClassNames.FluentIconCssClass} ${ClassNames.getCssClass(IconFontType.Sync)}`
            );

            announcement.primaryButton = {
                id: 'refresh-page-announcement',
                text: resources.SessionExpiredButtonTitle,
                ariaLabel: resources.SessionExpiredButtonTitle,
                onClick: () => {
                    this.windowService.nativeWindow.location.reload();
                }
            };

            this.eventService.broadcastEvent({
                name: AngularEvents.newAnnouncement,
                args: [ announcement ]
            });
        });
    }

    public getAnnouncements(): Array<AnnouncementModel> {
        if (this.announcements.length) {
            return this.announcements;
        }

        if (!this.announcements.length) {
            this.addInitialAnnouncements();
        }

        return this.announcements;
    }

    private addInitialAnnouncements(): void {
        const user = this.userContextService.user;
        if (!user.isValid) {
            this.registerKnownAnnouncements(AnnouncementType.MissingPuid);
        } else if (user.hasNoLicense) {
            this.registerKnownAnnouncements(AnnouncementType.NoLicense);
        } else if (user.hasTrialSubscription) {
            this.registerKnownAnnouncements(AnnouncementType.TrialSubscription);
        } else if (this.deviceDetector.isIE11Browser()) {
            this.registerKnownAnnouncements(AnnouncementType.IE11EndOfSupport);
        }
    }
}
